import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { TimePicker } from "antd";
import dayjs from "dayjs";

import { editAttendanceRecord } from "../../../../../services/attendanceService";
import { formatTimeCheckinOut } from "../../../../../helper/attendance_helper";
import { notify } from "../../../../../services/toast.config";

interface LogEntry {
  raw_time?: string | null;
  check_time?: string | null;
  ip_address?: string | null;
  is_manual?: boolean;
}

interface PunchPair {
  checkIn: string;
  checkOut: string;
  originalCheckIn: string;
  originalCheckOut: string;
  inIndex?: number; // Index in the allLogs array
  outIndex?: number; // Index in the allLogs array
  inIsManual?: boolean;
  outIsManual?: boolean;
}

interface EditAttendanceModalProps {
  visible: boolean;
  onClose: () => void;
  record: {
    id: number;
    user_id: number;
    essl_id: string;
    date: string;
    Employee?: string;
    employee_name?: string;
    CheckIn?: string;
    check_in_time?: string;
    CheckOut?: string;
    check_out_time?: string;
    logs?: LogEntry[];
  } | null;
  onSuccess: () => void;
}

const EditAttendanceModal: React.FC<EditAttendanceModalProps> = ({
  visible,
  onClose,
  record,
  onSuccess,
}) => {
  const [allLogs, setAllLogs] = useState<LogEntry[]>([]);
  const [punchPairs, setPunchPairs] = useState<PunchPair[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (record && visible) {
      const logs = record.logs || [];
      // We sort them for display but we'll manage them carefully
      const sortedWithOriginalIndex = logs
        .map((log, index) => ({ ...log, originalIndex: index }))
        .sort(
          (a, b) =>
            dayjs(a.raw_time || 0).valueOf() - dayjs(b.raw_time || 0).valueOf(),
        );

      setAllLogs([...logs]);

      const ci = record.CheckIn || record.check_in_time || "";
      const co = record.CheckOut || record.check_out_time || "";

      const punchLogs = sortedWithOriginalIndex;
      const pairs: PunchPair[] = [];

      if (punchLogs.length > 0) {
        for (let i = 0; i < punchLogs.length; i += 2) {
          const ciLog = punchLogs[i];
          const coLog = punchLogs[i + 1];
          pairs.push({
            checkIn: ciLog?.check_time || "",
            checkOut: coLog?.check_time || "",
            originalCheckIn: ciLog?.check_time || "",
            originalCheckOut: coLog?.check_time || "",
            inIndex: ciLog?.originalIndex,
            outIndex: coLog?.originalIndex,
            inIsManual: ciLog?.is_manual ?? false,
            outIsManual: coLog?.is_manual ?? false,
          });
        }
      } else if (ci || co) {
        pairs.push({
          checkIn: ci,
          checkOut: co,
          originalCheckIn: ci,
          originalCheckOut: co,
        });
      } else {
        // If completely empty, provide one blank pair for convenience
        pairs.push({
          checkIn: "",
          checkOut: "",
          originalCheckIn: "",
          originalCheckOut: "",
        });
      }
      setPunchPairs(pairs);
    }
  }, [record, visible]);

  const getCurrentLocation = (): Promise<GeolocationPosition> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation is not supported by your browser"));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        maximumAge: 0,
      });
    });
  };

  const getUserIpAddress = async () => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error("Failed to fetch IP Address:", error);
      return null;
    }
  };

  const handleAddPair = () => {
    setPunchPairs([
      ...punchPairs,
      { checkIn: "", checkOut: "", originalCheckIn: "", originalCheckOut: "" },
    ]);
  };

  const handlePunchChange = (
    index: number,
    field: "checkIn" | "checkOut",
    value: string,
  ) => {
    const updated = [...punchPairs];
    updated[index][field] = value;
    setPunchPairs(updated);
  };

  const handleDeletePair = (index: number) => {
    setPunchPairs(punchPairs.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!record) return;

    const initialPairsCount =
      record.logs && record.logs.length > 0
        ? Math.ceil(record.logs.length / 2)
        : record.CheckIn ||
            record.check_in_time ||
            record.CheckOut ||
            record.check_out_time
          ? 1
          : 0;

    const hasChanges =
      punchPairs.some(
        (p) =>
          p.checkIn !== p.originalCheckIn || p.checkOut !== p.originalCheckOut,
      ) || punchPairs.length !== initialPairsCount;

    if (!hasChanges) {
      notify.warn("No changes detected");
      return;
    }

    setLoading(true);
    try {
      let ip: string | null = null;
      let location: {
        latitude: number;
        longitude: number;
        accuracy: number;
      } | null = null;

      try {
        const position = await getCurrentLocation();
        location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
      } catch (locError: any) {
        console.error("Failed to capture location:", locError);
        let errorMessage = "Location access is required to edit attendance.";
        if (locError instanceof Error) {
          if (locError.message.includes("User denied Geolocation")) {
            errorMessage =
              "Please allow location access in your browser to edit attendance.";
          } else {
            errorMessage = locError.message;
          }
        }
        notify.error(errorMessage);
        setLoading(false);
        return;
      }

      try {
        ip = await getUserIpAddress();
      } catch (ipError) {
        console.error("Failed to fetch IP:", ipError);
      }

      // Construct the final log array for "canonical" logs (punches)
      const updatedLogs: LogEntry[] = [];

      punchPairs.forEach((pair) => {
        const datePrefix = record.date;
        if (pair.checkIn) {
          updatedLogs.push({
            check_time: pair.checkIn,
            raw_time: dayjs(`${datePrefix} ${pair.checkIn}`).toISOString(),
            ip_address: ip,
            is_manual: true,
          });
        }
        if (pair.checkOut) {
          updatedLogs.push({
            check_time: pair.checkOut,
            raw_time: dayjs(`${datePrefix} ${pair.checkOut}`).toISOString(),
            ip_address: ip,
            is_manual: true,
          });
        }
      });

      // Use first non-empty check-in and last non-empty check-out
      const finalCheckIn = punchPairs.find((p) => p.checkIn)?.checkIn || "";
      const finalCheckOut =
        [...punchPairs].reverse().find((p) => p.checkOut)?.checkOut || "";

      const response = await editAttendanceRecord({
        attendance_id: record.id,
        user_id: record.user_id,
        check_in: finalCheckIn,
        check_out: finalCheckOut,
        date: record.date,
        ip,
        location,
        updated_logs: updatedLogs,
      });

      if (response?.message?.message) {
        notify.success(response?.message?.message);
      } else {
        notify.success("Attendance updated successfully.");
      }
      onSuccess();
    } catch (error: any) {
      const errorMsg =
        error?.data?.message?.message ||
        error?.response?.data?.message?.message ||
        "Failed to update attendance";
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!record) return null;

  const displayDate = record.date
    ? dayjs(record.date).format("DD MMM YYYY (dddd)")
    : "";
  const employeeName = record.Employee || record.employee_name || "Employee";

  return (
    <Modal
      show={visible}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard
      size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Attendance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <span className="fw-semibold">{employeeName}</span>
            <span className="text-muted ms-2">{displayDate}</span>
          </div>
          {!punchPairs.some(
            (p) =>
              (p.originalCheckIn && !p.inIsManual) ||
              (p.originalCheckOut && !p.outIsManual),
          ) && (
            <Button variant="outline-primary" size="sm" onClick={handleAddPair}>
              <i className="ti ti-plus me-1"></i> Add Pair
            </Button>
          )}
        </div>

        <div
          className="punch-pairs-container"
          style={{ maxHeight: "400px", overflowY: "auto" }}>
          {punchPairs.length === 0 ? (
            <div className="text-center py-4 text-muted border rounded bg-light">
              No punch records. Click "Add Pair" to record attendance.
            </div>
          ) : (
            punchPairs.map((pair, index) => (
              <div key={index} className="card mb-3 border-light shadow-sm">
                <div className="card-body p-3">
                  <div className="row align-items-end">
                    <div className="col-md-5">
                      <Form.Group>
                        <Form.Label
                          className="small fw-bold text-secondary text-uppercase"
                          style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                          Punch In {index + 1}
                        </Form.Label>
                        <TimePicker
                          value={
                            pair.checkIn
                              ? dayjs(`2000-01-01T${pair.checkIn}`)
                              : null
                          }
                          onChange={(_time, timeString) =>
                            handlePunchChange(
                              index,
                              "checkIn",
                              timeString as string,
                            )
                          }
                          format="HH:mm:ss"
                          className="w-100 shadow-sm text-center fw-medium"
                          size="large"
                          style={{
                            borderRadius: "8px",
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                            height: "42px",
                          }}
                          popupClassName="modern-time-picker"
                          allowClear
                          showNow={false}
                        />
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          {pair.originalCheckIn && (
                            <Form.Text className="text-muted x-small m-0">
                              Original:{" "}
                              {formatTimeCheckinOut(pair.originalCheckIn)}
                            </Form.Text>
                          )}
                          {pair.originalCheckIn ? (
                            pair.inIsManual ? (
                              <span className="badge bg-soft-info text-info x-small">
                                Manual
                              </span>
                            ) : (
                              <span className="badge bg-soft-success text-success x-small">
                                Biometric
                              </span>
                            )
                          ) : pair.checkIn ? (
                            <span className="badge bg-soft-warning text-warning x-small">
                              Unsaved
                            </span>
                          ) : null}
                        </div>
                      </Form.Group>
                    </div>
                    <div className="col-md-5">
                      <Form.Group>
                        <Form.Label
                          className="small fw-bold text-secondary text-uppercase"
                          style={{ fontSize: "11px", letterSpacing: "0.5px" }}>
                          Punch Out {index + 1}
                        </Form.Label>
                        <TimePicker
                          value={
                            pair.checkOut
                              ? dayjs(`2000-01-01T${pair.checkOut}`)
                              : null
                          }
                          onChange={(_time, timeString) =>
                            handlePunchChange(
                              index,
                              "checkOut",
                              timeString as string,
                            )
                          }
                          format="HH:mm:ss"
                          className="w-100 shadow-sm text-center fw-medium"
                          size="large"
                          style={{
                            borderRadius: "8px",
                            backgroundColor: "#f8f9fa",
                            cursor: "pointer",
                            height: "42px",
                          }}
                          popupClassName="modern-time-picker"
                          allowClear
                          showNow={false}
                        />
                        <div className="d-flex justify-content-between align-items-center mt-2">
                          {pair.originalCheckOut && (
                            <Form.Text className="text-muted x-small m-0">
                              Original:{" "}
                              {formatTimeCheckinOut(pair.originalCheckOut)}
                            </Form.Text>
                          )}
                          {pair.originalCheckOut ? (
                            pair.outIsManual ? (
                              <span className="badge bg-soft-info text-info x-small">
                                Manual
                              </span>
                            ) : (
                              <span className="badge bg-soft-success text-success x-small">
                                Biometric
                              </span>
                            )
                          ) : pair.checkOut ? (
                            <span className="badge bg-soft-warning text-warning x-small">
                              Unsaved
                            </span>
                          ) : null}
                        </div>
                      </Form.Group>
                    </div>
                    <div className="col-md-2 text-end">
                      {!(
                        (pair.originalCheckIn && !pair.inIsManual) ||
                        (pair.originalCheckOut && !pair.outIsManual)
                      ) && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeletePair(index)}>
                          <i className="ti ti-trash"></i>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditAttendanceModal;
